// Fetch tipizzati del modulo Clienti. Backend GIÀ pronto:
//   GET /api/customers?q=&limit=&offset=  -> CustomerListItem[]
//   GET /api/customers/{key}              -> CustomerDetail
// La ``key`` è opaca ('e:<email>' / 'f:<name>|<city>') e va SEMPRE encodata
// nell'URL (contiene ':', '@', '|', spazi).

import { apiGet, apiPatch, apiPost } from "@/shared/api";
import { mapOrder } from "@/features/ordini/api";
import type { CustomerDetail, CustomerListItem, CustomerSegment, TimelineItem } from "./types";

// ── Shape backend (snake_case) ────────────────────────────────────────────

interface ApiCustomerListItem {
  key: string;
  name: string;
  email: string;
  city: string;
  orders_count: number;
  ltv: string;
  ltv_currency: string;
  last_order_at: string | null;
}

// Righe/chiamate arrivano dal router come dict "loose" (vedi CustomerDetail
// backend): tipizziamo solo i campi che consumiamo.
interface ApiCustomerReturn {
  id: number;
  kind: string;
  status: string;
  order_reference: string;
  created_at: string | null;
}

interface ApiCustomerCall {
  id: number;
  order_reference: string | null;
  reason: string;
  note: string | null;
  created_at: string | null;
}

interface ApiCustomerDetail {
  profile: {
    name: string;
    email: string;
    phone: string;
    city: string;
    is_fallback_key: boolean;
    note: string;
    tags: string[];
  };
  kpis: {
    ltv: string;
    ltv_currency: string;
    orders_count: number;
    first_order_at: string | null;
    last_order_at: string | null;
  };
  // ``mapOrder`` accetta ApiOrder: tipizzato come parametro per non ri-esportarlo.
  orders: Parameters<typeof mapOrder>[0][];
  returns: ApiCustomerReturn[];
  calls: ApiCustomerCall[];
  replacements: { count: number; cost_total: number };
  aliases: string[];
}

/** Body PATCH profilo (snake_case = backend ``CustomerProfilePatch``). Solo i
 *  campi PRESENTI vengono aggiornati (exclude_unset lato router): il chiamante
 *  invia esclusivamente ciò che è cambiato. */
export interface CustomerProfilePatch {
  display_name?: string;
  phone?: string;
  note?: string;
  tags?: string[];
}

interface ApiTimelineItem {
  ts: string;
  kind: string;
  title: string;
  link: string | null;
  meta: string | null;
}

// ── Mapper snake → camel ──────────────────────────────────────────────────

function mapListItem(row: ApiCustomerListItem): CustomerListItem {
  return {
    key: row.key,
    name: row.name,
    email: row.email,
    city: row.city,
    ordersCount: row.orders_count,
    ltv: Number(row.ltv),
    ltvCurrency: row.ltv_currency,
    lastOrderAt: row.last_order_at,
  };
}

function mapDetail(res: ApiCustomerDetail): CustomerDetail {
  return {
    profile: {
      name: res.profile.name,
      email: res.profile.email,
      phone: res.profile.phone,
      city: res.profile.city,
      isFallbackKey: res.profile.is_fallback_key,
      note: res.profile.note,
      tags: res.profile.tags,
    },
    kpis: {
      ltv: Number(res.kpis.ltv),
      ltvCurrency: res.kpis.ltv_currency,
      ordersCount: res.kpis.orders_count,
      firstOrderAt: res.kpis.first_order_at,
      lastOrderAt: res.kpis.last_order_at,
    },
    orders: res.orders.map(mapOrder),
    returns: res.returns.map((r) => ({
      id: r.id,
      kind: r.kind,
      status: r.status,
      orderReference: r.order_reference,
      createdAt: r.created_at,
    })),
    calls: res.calls.map((c) => ({
      id: c.id,
      orderReference: c.order_reference,
      reason: c.reason,
      note: c.note,
      createdAt: c.created_at,
    })),
    replacements: { count: res.replacements.count, costTotal: res.replacements.cost_total },
    aliases: res.aliases ?? [],
  };
}

// ── API ───────────────────────────────────────────────────────────────────

export async function fetchCustomers(
  params: { q: string; limit: number; offset: number; segment?: CustomerSegment | null },
  signal?: AbortSignal,
): Promise<CustomerListItem[]> {
  const qs = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.q.trim()) qs.set("q", params.q.trim());
  if (params.segment) qs.set("segment", params.segment);
  const rows = await apiGet<ApiCustomerListItem[]>(
    `/api/customers?${qs.toString()}`,
    signal ? { signal } : undefined,
  );
  return rows.map(mapListItem);
}

/** PATCH profilo CRM (nome/telefono/note/tag). Solo chiavi email: sul match
 *  debole il backend risponde 422 (nessun cliente stabile). Il body arriva già
 *  ridotto ai campi cambiati; la response (profilo+alias) non viene consumata —
 *  la scheda rifà il fetch del dettaglio per riconciliare KPI/H1/alias. */
export async function patchCustomerProfile(
  key: string,
  patch: CustomerProfilePatch,
): Promise<void> {
  await apiPatch(`/api/customers/${encodeURIComponent(key)}/profile`, patch);
}

/** Fonde ``otherKey`` in ``key``: l'email dell'altro diventa alias. Entrambe
 *  devono essere chiavi email ('e:...'); self-merge/catene → 422 dal backend. */
export async function mergeCustomers(key: string, otherKey: string): Promise<void> {
  await apiPost(`/api/customers/${encodeURIComponent(key)}/merge`, { other_key: otherKey });
}

/** Timeline unificata del cliente (ordini/chiamate/resi/sostituzioni, DESC). */
export async function fetchCustomerTimeline(
  key: string,
  signal?: AbortSignal,
): Promise<TimelineItem[]> {
  const rows = await apiGet<ApiTimelineItem[]>(
    `/api/customers/${encodeURIComponent(key)}/timeline`,
    signal ? { signal } : undefined,
  );
  return rows.map((r) => ({
    ts: r.ts,
    kind: r.kind,
    title: r.title,
    link: r.link,
    meta: r.meta,
  }));
}

export async function fetchCustomerDetail(
  key: string,
  signal?: AbortSignal,
): Promise<CustomerDetail> {
  const res = await apiGet<ApiCustomerDetail>(
    `/api/customers/${encodeURIComponent(key)}`,
    signal ? { signal } : undefined,
  );
  return mapDetail(res);
}

// Riesportata (non ridefinita): i quattro consumatori di clienti importano
// `formatDateIt` da qui e non serve toccarli.
export { formatDateIt } from "@/shared/format";
